import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { services } from '../utils/mockData';

const ServicesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(services.map(s => s.industries[0]))];

  const filteredServices = selectedCategory === 'All' 
    ? services 
    : services.filter(s => s.industries.includes(selectedCategory));

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Services</h1>
            <p className="text-xl text-slate-300">
              Comprehensive electrical and automation solutions for your industrial needs
            </p>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-lg font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service) => {
              const IconComponent = Icons[service.icon];
              return (
                <div
                  key={service.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-blue-600 w-12 h-12 rounded-lg flex items-center justify-center mb-3">
                        <IconComponent className="text-white" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <p className="text-slate-600 mb-4 leading-relaxed">
                      {service.description}
                    </p>
                    
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Industries Served:</p>
                      <div className="flex flex-wrap gap-2">
                        {service.industries.map((industry, index) => (
                          <span 
                            key={index} 
                            className="text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full"
                          >
                            {industry}
                          </span>
                        ))}
                      </div>
                    </div>

                    <a
                      href="/contact"
                      className="inline-flex items-center text-blue-600 font-semibold hover:text-blue-700 transition-colors"
                    >
                      Get Quote
                      <Icons.ArrowRight size={18} className="ml-1" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Features */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What We Offer
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Complete electrical solutions with professional service
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-slate-50 p-6 rounded-xl text-center">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.CheckCircle className="text-blue-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Quality Components</h3>
              <p className="text-slate-600 text-sm">
                Genuine parts from trusted brands
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl text-center">
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.Clock className="text-green-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Quick Turnaround</h3>
              <p className="text-slate-600 text-sm">
                Fast installation and repair services
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.HeadphonesIcon className="text-orange-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Expert Support</h3>
              <p className="text-slate-600 text-sm">
                24/7 technical assistance available
              </p>
            </div>

            <div className="bg-slate-50 p-6 rounded-xl text-center">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icons.Shield className="text-purple-600" size={28} />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Safety Certified</h3>
              <p className="text-slate-600 text-sm">
                All work meets safety standards
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Need a Custom Solution?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Contact us for customized electrical and automation solutions tailored to your requirements
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all shadow-lg"
          >
            Get Free Consultation
            <Icons.ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
